import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMediaCoverageData, formatDate, convertDriveUrl } from '../../services/googleSheets';
import '../NoticeList.css';
import ShareButton from '../ShareButton';
import URLPreview from '../URLPreview';

interface MediaCoverageItem {
  id: number;
  카테고리: string;
  제목: string;
  내용: string;
  작성일: string;
  첨부파일URL?: string;
  첨부파일이름?: string;
  언론사?: string;
  외부링크?: string;
}

const MediaCoverage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mediaCoverage, setMediaCoverage] = useState<MediaCoverageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string>('전체');
  const [selectedCoverage, setSelectedCoverage] = useState<MediaCoverageItem | null>(null);
  const itemsPerPage = 10;

  const categories = ['전체', '신문', 'TV', '온라인', '라디오'];
  
  // 언론사별 아이콘 매핑
  const getMediaIcon = (mediaType: string) => {
    if (mediaType?.includes('신문')) return '📰';
    if (mediaType?.includes('TV') || mediaType?.includes('방송')) return '📺';
    if (mediaType?.includes('온라인') || mediaType?.includes('인터넷')) return '💻';
    if (mediaType?.includes('라디오')) return '📻';
    return '📄';
  };

  useEffect(() => {
    const fetchMediaCoverage = async () => {
      try {
        const data = await getMediaCoverageData();
        // 최신순으로 정렬 (언론보도 시트에서 가져오므로 별도 필터링 불필요)
        const sortedData = data.sort((a: any, b: any) => b.id - a.id);
        setMediaCoverage(sortedData as MediaCoverageItem[]);
        
        // URL에 id가 있으면 해당 언론보도를 선택
        if (id) {
          const coverage = sortedData.find((item: any) => item.id === parseInt(id));
          if (coverage) {
            setSelectedCoverage(coverage as MediaCoverageItem);
          }
        }
      } catch (error) {
        console.error('언론보도를 불러오는데 실패했습니다:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMediaCoverage();
  }, [id]);



  // 카테고리별 필터링 (카테고리 필드 기준으로 변경)
  const filteredCoverage = selectedCategory === '전체' 
    ? mediaCoverage 
    : mediaCoverage.filter(item => item.카테고리?.includes(selectedCategory));

  // 페이지네이션 계산
  const totalPages = Math.ceil(filteredCoverage.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredCoverage.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleCoverageClick = (coverage: MediaCoverageItem) => {
    navigate(`/media-coverage/${coverage.id}`);
  };

  const handleBackToList = () => {
    setSelectedCoverage(null);
    navigate('/media-coverage');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // YouTube URL을 embed URL로 변환하는 함수
  const getYouTubeEmbedUrl = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    return null;
  };

  // 이미지 URL인지 확인하는 함수
  const isImageUrl = (url: string) => {
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(url);
  };

  // YouTube URL인지 확인하는 함수
  const isYouTubeUrl = (url: string) => {
    return /(?:youtube\.com\/watch\?v=|youtu\.be\/)/.test(url);
  };

  // 첨부파일을 미디어로 렌더링하는 함수
  const renderAttachmentAsMedia = (url: string, fileName?: string) => {
    if (!url) return null;

    const processedUrl = convertDriveUrl(url) || url;

    if (isYouTubeUrl(url)) {
      const embedUrl = getYouTubeEmbedUrl(url);
      if (embedUrl) {
        return (
          <div style={{ margin: '2rem 0' }}>
            <iframe
              width="100%"
              height="400"
              src={embedUrl}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            />
          </div>
        );
      }
    } else if (isImageUrl(processedUrl)) {
      return (
        <div style={{ margin: '2rem 0', textAlign: 'center' }}>
          <img
            src={processedUrl}
            alt={fileName || '첨부 이미지'}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          {fileName && (
            <p style={{ 
              marginTop: '0.5rem', 
              fontSize: '0.9rem', 
              color: '#666',
              fontStyle: 'italic'
            }}>
              {fileName}
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  // 첨부파일 표시 컴포넌트
  const AttachmentDisplay: React.FC<{ url: string; fileName?: string }> = ({ url, fileName }) => {
    const downloadUrl = convertDriveUrl(url) || url;
    const displayName = fileName || '첨부파일';
    
    return (
      <a 
        href={downloadUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        className="notice-attachment-inline"
        title={displayName}
      >
        <span className="attachment-icon">📎</span>
        <span className="attachment-name">{displayName}</span>
      </a>
    );
  };

  if (loading) {
    return (
      <div className="notice-loading">
        <div className="loading-spinner"></div>
        <p>언론보도를 불러오는 중...</p>
      </div>
    );
  }

  // 상세보기 화면
  if (selectedCoverage) {
    return (
      <div className="notice-list-container">
        <div className="notice-header">
          <h1>📺 언론보도</h1>
          <p>청소년 스마트폰 프리 운동본부 관련 언론 보도 자료를 확인하세요</p>
        </div>
        <div className="notice-list" style={{ padding: '2rem' }}>
          <div className="notice-header" style={{ marginBottom: '2rem', textAlign: 'left' }}>
            <h1 style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>{selectedCoverage.제목}</h1>
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              fontSize: '0.9rem', 
              color: '#666',
              paddingBottom: '1rem',
              borderBottom: '2px solid #e74c3c',
              flexWrap: 'wrap'
            }}>
              <span>📅 {formatDate(selectedCoverage.작성일)}</span>
              <span>
                {getMediaIcon(selectedCoverage.카테고리 || '')} {selectedCoverage.카테고리}
              </span>
              <span>📰 {selectedCoverage.언론사}</span>
            </div>
          </div>
          
          {/* 첨부파일이 이미지나 YouTube인 경우 상단에 표시 */}
          {selectedCoverage.첨부파일URL && renderAttachmentAsMedia(selectedCoverage.첨부파일URL, selectedCoverage.첨부파일이름)}
          
          <div style={{ 
            lineHeight: '1.8', 
            color: '#2c3e50', 
            marginBottom: '2rem',
            whiteSpace: 'pre-line'
          }}>
            {selectedCoverage.내용}
          </div>
          
          {selectedCoverage.외부링크 && (
            <div style={{
              borderTop: '1px solid #eee',
              paddingTop: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h3 style={{ color: '#2c3e50', marginBottom: '1rem', fontSize: '1.1rem' }}>
                원본 기사
              </h3>
              <a 
                href={selectedCoverage.외부링크} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#007bff',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #007bff',
                  borderRadius: '25px',
                  transition: 'all 0.2s',
                  fontWeight: '600'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#007bff';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#007bff';
                }}
              >
                <span>🔗</span>
                <span>원본 기사 보기</span>
              </a>
            </div>
          )}
          
          <div style={{
            borderTop: '1px solid #eee',
            paddingTop: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <button
              onClick={handleBackToList}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#5a6268'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#6c757d'}
            >
              목록
            </button>
            <ShareButton title={`${selectedCoverage.제목} - 청소년 스마트폰 프리 운동`} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="notice-list-container">
      <div className="notice-header">
        <h1>📺 언론보도</h1>
        <p>청소년 스마트폰 프리 운동본부 관련 언론 보도 자료를 확인하세요</p>
      </div>

      {/* 카테고리 필터 */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '2rem',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        {categories.map(category => (
          <button
            key={category}
            onClick={() => handleCategoryChange(category)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              border: '2px solid #e74c3c',
              background: selectedCategory === category ? '#e74c3c' : 'white',
              color: selectedCategory === category ? 'white' : '#e74c3c',
              borderRadius: '25px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s',
              fontSize: '0.9rem'
            }}
          >
            <span>{getMediaIcon(category)}</span>
            <span>{category}</span>
          </button>
        ))}
      </div>

      <div className="notice-stats">
        <span>
          {selectedCategory === '전체' ? '전체' : selectedCategory} {filteredCoverage.length}건
          {selectedCategory !== '전체' && (
            <span style={{ color: '#666', marginLeft: '0.5rem' }}>
              (전체 {mediaCoverage.length}건 중)
            </span>
          )}
        </span>
        <span>페이지 {currentPage} / {totalPages}</span>
      </div>

      <div className="notice-list">
        {currentItems.length > 0 ? (
          <>
            {/* 데스크톱 테이블 뷰 */}
            <table className="notice-table">
              <thead>
                <tr>
                  <th className="notice-number">번호</th>
                  <th className="notice-title-header">제목</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>분류</th>
                  <th style={{ width: '140px', textAlign: 'center' }}>언론사</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>링크</th>
                  <th className="notice-date-header">보도일</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item) => (
                  <tr key={item.id} className="notice-row">
                    <td className="notice-number">{item.id}</td>
                    <td className="notice-title-cell">
                      <button 
                        onClick={() => handleCoverageClick(item)}
                        className="notice-link"
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer',
                          textAlign: 'left',
                          width: '100%',
                          padding: 0
                        }}
                      >
                        {item.제목 || '제목 없음'}
                      </button>
                    </td>
                    <td style={{ 
                      textAlign: 'center', 
                      color: '#666', 
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}>
                      <span style={{ marginRight: '0.25rem' }}>
                        {getMediaIcon(item.카테고리 || '')}
                      </span>
                      {item.카테고리 || '-'}
                    </td>
                    <td style={{ 
                      textAlign: 'center', 
                      color: '#2c3e50', 
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}>
                      {item.언론사 || '-'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {item.외부링크 ? (
                        <a 
                          href={item.외부링크} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '60px',
                            height: '32px',
                            background: '#007bff',
                            color: 'white',
                            textDecoration: 'none',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            borderRadius: '16px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#0056b3';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#007bff';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          🔗 보기
                        </a>
                      ) : (
                        item.첨부파일URL ? (
                          <AttachmentDisplay 
                            url={item.첨부파일URL} 
                            fileName={item.첨부파일이름}
                          />
                        ) : (
                          <span style={{ color: '#ccc', fontSize: '0.8rem' }}>-</span>
                        )
                      )}
                    </td>
                    <td className="notice-date-cell">{formatDate(item.작성일)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 모바일 카드 뷰 */}
            <div className="notice-cards">
              {currentItems.map((item) => (
                <div key={item.id} className="notice-card">
                  <div className="notice-card-header">
                    <div className="notice-card-info">
                      <span className="notice-card-number">
                        📺 {item.id}
                      </span>
                      <span className="notice-card-date">
                        📅 {formatDate(item.작성일)}
                      </span>
                      <span className="notice-card-category">
                        {getMediaIcon(item.카테고리 || '')} {item.카테고리 || '기타'}
                      </span>
                      {item.언론사 && (
                        <span style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          background: '#28a745',
                          color: 'white',
                          padding: '0.2rem 0.6rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          lineHeight: '1'
                        }}>
                          🏢 {item.언론사}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div 
                    className="notice-card-title"
                    onClick={() => handleCoverageClick(item)}
                  >
                    {item.제목 || '제목 없음'}
                  </div>
                  
                  {item.내용 && (
                    <div className="notice-card-content">
                      {item.내용.substring(0, 100)}...
                    </div>
                  )}
                  
                  <div className="notice-card-footer">
                    <div></div>
                    {item.외부링크 ? (
                      <a 
                        href={item.외부링크} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          background: '#007bff',
                          color: 'white',
                          textDecoration: 'none',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          padding: '0.5rem 1rem',
                          borderRadius: '16px',
                          transition: 'all 0.2s'
                        }}
                      >
                        🔗 원문보기
                      </a>
                    ) : (
                      item.첨부파일URL && (
                        <AttachmentDisplay 
                          url={item.첨부파일URL} 
                          fileName={item.첨부파일이름}
                        />
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="notice-empty">
            <p>해당 카테고리의 언론보도가 없습니다.</p>
          </div>
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            ‹ 이전
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
            >
              {page}
            </button>
          ))}
          
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            다음 ›
          </button>
        </div>
      )}
    </div>
  );
};

export default MediaCoverage; 